import React, { useState, useEffect } from "react";
import { Helmet } from 'react-helmet';
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { FaTaxi, FaCar, FaUserFriends, FaInfoCircle, FaArrowLeft, FaArrowRight } from "react-icons/fa";
import taxiBg from "../../Assets/img/taxi_cat.png";
import CategoryBookingPopup from "../../components/common/CategoryBookingPopup";

// Import Local Assets
import img1 from "../../Assets/img/innova-crysta-taxi-khajuraho.jpg";
import img2 from "../../Assets/img/swift-dzire-taxi-khajuraho.png";
import img3 from "../../Assets/img/tempo-traveller-rental-khajuraho.jpeg";
import img4 from "../../Assets/img/luxury-bus-rental-khajuraho.jpg";


const Taxi = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [popupData, setPopupData] = useState({ isOpen: false, itemName: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const vehicles = [
    { id: 1, name: "Toyota Innova Crysta", type: "Premium SUV", capacity: "6+1 Pax", desc: "Top-tier comfort for long journeys and family trips.", img: img1 },
    { id: 2, name: "Swift Dzire / Etios", type: "Executive Sedan", capacity: "4+1 Pax", desc: "Economical and comfortable for local transfers.", img: img2 },
    { id: 3, name: "Force Tempo Traveller", type: "Luxury Van", capacity: "12-26 Pax", desc: "Ideal for large groups, pilgrims, and wedding parties.", img: img3 },
    { id: 4, name: "Luxury Bus", type: "Premium Coach", capacity: "17+1 Pax", desc: "Ultra-luxury travel with push-back seats and AC.", img: img4 },
   ];

  const totalPages = Math.ceil(vehicles.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentVehicles = vehicles.slice(startIndex, startIndex + itemsPerPage);

  const handleOpenPopup = (name) => {
    setPopupData({ isOpen: true, itemName: name });
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 300, behavior: 'smooth' });
  };

  return (
    <main className="bg-[#FCFBFA] min-h-screen">
      <Helmet>
        <title>Khajuraho Taxi Service, Cabs & Car Rental | Khajuraho Routes</title>
        <meta name="description" content="Book taxi service in Khajuraho for airport transfers, temple sightseeing, Panna safari and Orchha trips. Sedans, SUVs and Tempo Travellers with experienced local drivers." />
        <meta name="keywords" content="Khajuraho taxi service, Khajuraho cab booking, Khajuraho airport taxi, Panna Orchha taxi, Innova on rent Khajuraho" />
        <link rel="canonical" href="https://khajurahoroutes.com/taxi" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://khajurahoroutes.com/taxi" />
        <meta property="og:title" content="Khajuraho Taxi Service, Cabs & Car Rental | Khajuraho Routes" />
        <meta property="og:description" content="Book taxi service in Khajuraho for airport transfers, temple sightseeing, Panna safari and Orchha trips. Sedans, SUVs and Tempo Travellers with experienced local drivers." />
        <meta property="og:image" content="https://khajurahoroutes.com/android-chrome-512x512.png" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Khajuraho Taxi Service, Cabs & Car Rental | Khajuraho Routes" />
        <meta name="twitter:description" content="Book taxi service in Khajuraho for airport transfers, temple sightseeing, Panna safari and Orchha trips. Sedans, SUVs and Tempo Travellers with experienced local drivers." />
        <meta name="twitter:image" content="https://khajurahoroutes.com/android-chrome-512x512.png" />

        {/* JSON-LD Schema */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Service",
              "name": "Khajuraho Routes Taxi Service",
              "provider": {
                "@type": "TravelAgency",
                "name": "Khajuraho Routes"
              },
              "areaServed": {
                "@type": "City",
                "name": "Khajuraho"
              },
              "description": "Premium taxi and cab rental services in Khajuraho."
            }
          `}
        </script>
      </Helmet>
      {/* HERO */}
      <section className="relative h-[50vh] sm:h-[45vh] flex items-start bg-black pt-16 md:pt-24">
        <img src={taxiBg} className="absolute inset-0 w-full h-full object-cover opacity-60" alt="Khajuraho taxi service - airport cab and car rental" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#FCFBFA] via-transparent to-transparent" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full mt-6 md:mt-10">
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/80 hover:text-orange-main transition-colors mb-4 md:mb-6 group"
          >
            <div className="w-6 h-6 md:w-8 md:h-8 rounded-full border border-white/20 flex items-center justify-center group-hover:border-orange-main transition-colors">
              <FaArrowLeft className="text-[8px] md:text-xs" />
            </div>
            <span className="text-[8px] md:text-[10px] font-black uppercase tracking-widest">Go Back</span>
          </motion.button>

          <div className="text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="flex items-center justify-center gap-2 md:gap-4 mb-2 md:mb-4">
                <div className="h-[1px] md:h-[2px] w-8 md:w-12 bg-orange-main" />
                <span className="text-orange-main font-black tracking-[0.2em] md:tracking-[0.4em] text-[8px] md:text-[10px] uppercase">On the Road</span>
                <div className="h-[1px] md:h-[2px] w-8 md:w-12 bg-orange-main" />
              </div>
              <h1 className="text-3xl md:text-6xl font-black text-white uppercase tracking-tight">Premium <span className="text-orange-main">Taxi</span></h1>
            </motion.div>
          </div>
        </div>
      </section>

      {/* GRID SECTION */}
      <section className="py-12 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-8">
            {currentVehicles.map((car) => (
              <motion.div 
                key={car.id}
                whileHover={{ y: -8 }}
                onClick={() => handleOpenPopup(t(car.name))}
                className="group relative h-[300px] sm:h-[380px] rounded-2xl md:rounded-[2.5rem] overflow-hidden cursor-pointer shadow-lg hover:shadow-2xl transition-all duration-500 border-2 border-white"
              >
                {/* Image */}
                <img 
                  src={car.img} 
                  alt={t(car.name)} 
                  className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" 
                />
                
                {/* Initial Name Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-8 bg-gradient-to-t from-black/80 via-black/20 to-transparent group-hover:opacity-0 transition-opacity duration-300">
                   <h3 className="text-lg md:text-xl font-black text-white uppercase tracking-tight leading-none">{t(car.name)}</h3>
                   <div className="flex items-center gap-2 mt-1 md:mt-2 text-gray-300 text-[8px] md:text-[9px] font-bold uppercase tracking-widest">
                     <FaCar className="text-orange-main" /> {t(car.type)}
                   </div>
                </div>

                {/* Hover Detailed Overlay */}
                <div className="absolute inset-0 bg-orange-main/90 flex flex-col justify-center items-center p-6 md:p-8 text-center opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-4 group-hover:translate-y-0">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-white rounded-xl md:rounded-2xl flex items-center justify-center text-orange-main mb-4 md:mb-6 shadow-xl">
                    <FaTaxi size={16} />
                  </div>
                  <h3 className="text-xl md:text-2xl font-black text-white uppercase tracking-tight mb-2 leading-none">{t(car.name)}</h3>
                  <div className="flex items-center gap-3 text-white/80 text-[8px] md:text-[10px] font-black uppercase tracking-widest mb-3 md:mb-4">
                    <span className="flex items-center gap-2"><FaUserFriends /> {t(car.capacity)}</span>
                    <span className="w-1 h-1 bg-white/40 rounded-full" />
                    <span>{t(car.type)}</span>
                  </div>
                  <div className="h-px w-8 md:w-12 bg-white/30 mb-4 md:mb-6" />
                  <p className="text-white text-[10px] md:text-xs font-bold leading-relaxed mb-6 md:mb-8">
                    {t(car.desc)}
                  </p>
                  <div className="px-4 py-2 md:px-6 md:py-3 border-2 border-white rounded-full text-white text-[8px] md:text-[10px] font-black uppercase tracking-widest">
                    Book Now
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* PAGINATION */}
          {totalPages > 1 && (
            <div className="mt-16 flex justify-center items-center gap-4">
              <button 
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`flex items-center gap-2 px-6 py-3 rounded-full font-black text-[10px] uppercase tracking-widest transition-all ${
                  currentPage === 1 
                  ? "bg-gray-100 text-gray-300 cursor-not-allowed" 
                  : "bg-white text-orange-main border-2 border-orange-main hover:bg-orange-main hover:text-white shadow-lg"
                }`}
              >
                <FaArrowLeft size={10} /> Previous
              </button>
              
              <div className="flex items-center gap-2">
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => handlePageChange(i + 1)}
                    className={`w-10 h-10 rounded-full font-black text-xs transition-all ${
                      currentPage === i + 1
                      ? "bg-orange-main text-white shadow-xl scale-110"
                      : "bg-white text-gray-400 hover:text-orange-main"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>

              <button 
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`flex items-center gap-2 px-6 py-3 rounded-full font-black text-[10px] uppercase tracking-widest transition-all ${
                  currentPage === totalPages 
                  ? "bg-gray-100 text-gray-300 cursor-not-allowed" 
                  : "bg-white text-orange-main border-2 border-orange-main hover:bg-orange-main hover:text-white shadow-lg"
                }`}
              >
                Next <FaArrowRight size={10} />
              </button>
            </div>
          )}
        </div>
      </section>

      <CategoryBookingPopup 
        isOpen={popupData.isOpen} 
        onClose={() => setPopupData({ ...popupData, isOpen: false })}
        category="Taxi"
        itemName={popupData.itemName}
      />
    </main>
  );
};

export default Taxi;
